import { useNavigate } from "@tanstack/react-router";
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  FolderOpen,
  Loader2,
  MessageCircleQuestion,
  Plus,
  SlidersHorizontal,
  Users,
  X,
} from "lucide-react";
import { memo, useEffect, useMemo, useState } from "react";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { ResizableTh } from "@/components/shared/resizable-th";
import { SearchInput } from "@/components/shared/search-input";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAudits } from "@/hooks/use-audits";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import type { Project } from "@/hooks/use-projects";
import { useAllProjects } from "@/hooks/use-projects";
import type { ResizableColumn } from "@/hooks/use-resizable-columns";
import { useResizableColumns } from "@/hooks/use-resizable-columns";
import { useSession } from "@/lib/auth-client";
import { ProjectTeamDialog } from "./ProjectTeamDialog";

type SortKey =
  | "projectLead"
  | "demand"
  | "demandEndDate"
  | "product"
  | "countTotal"
  | "totalComplete"
  | "complianceRate";
type SortDir = "asc" | "desc";

interface ProjectsListProps {
  canCreateAudit?: boolean;
  onCreateAudit?: (projectId: string) => void;
  onRequestGuidance?: (projectId: string) => void;
}

interface ProjectRowProps {
  project: Project;
  canCreateAudit?: boolean;
  onCreateAudit?: (projectId: string) => void;
  onRequestGuidance?: (projectId: string) => void;
  onShowTeam: (project: Project) => void;
}

interface Filters {
  projectLead: string;
  demand: string;
  product: string;
  endDateMin: string;
  endDateMax: string;
  scopeMin: string;
  scopeMax: string;
  completedMin: string;
  completedMax: string;
  rateMin: string;
  rateMax: string;
}

const EMPTY_FILTERS: Filters = {
  projectLead: "",
  demand: "",
  product: "",
  endDateMin: "",
  endDateMax: "",
  scopeMin: "",
  scopeMax: "",
  completedMin: "",
  completedMax: "",
  rateMin: "",
  rateMax: "",
};

function activeFilterCount(filters: Filters): number {
  return Object.values(filters).filter((v) => v !== "").length;
}

function uniqueSorted(values: (string | null)[]): string[] {
  return [...new Set(values.filter((v): v is string => v !== null && v !== ""))].sort();
}

function FilterSelect({
  label,
  value,
  onValueChange,
  options,
}: {
  label: string;
  value: string;
  onValueChange: (v: string) => void;
  options: string[];
}) {
  return (
    <div className="flex flex-col gap-1">
      <Label className="text-xs text-muted-foreground">{label}</Label>
      <Select value={value} onValueChange={onValueChange}>
        <SelectTrigger size="sm" className="w-[160px]">
          <SelectValue placeholder={`All ${label}s`} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="_all">All {label}s</SelectItem>
          {options.map((opt) => (
            <SelectItem key={opt} value={opt}>
              {opt}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function SortButton({
  label,
  col,
  currentKey,
  currentDir,
  onSort,
}: {
  label: string;
  col: SortKey;
  currentKey: SortKey;
  currentDir: SortDir;
  onSort: (col: SortKey) => void;
}) {
  const isActive = currentKey === col;
  const Icon = isActive ? (currentDir === "asc" ? ArrowUp : ArrowDown) : ArrowUpDown;
  return (
    <button
      type="button"
      onClick={() => onSort(col)}
      className="flex items-center gap-1 hover:text-foreground transition-colors select-none"
    >
      {label}
      <Icon className={`h-3.5 w-3.5 ${isActive ? "text-foreground" : "opacity-50"}`} />
    </button>
  );
}

function ProjectRow({
  project,
  canCreateAudit,
  onCreateAudit,
  onRequestGuidance,
  onShowTeam,
}: ProjectRowProps) {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!isLoading) return;
    const timer = setTimeout(() => setIsLoading(false), 1000);
    return () => clearTimeout(timer);
  }, [isLoading]);

  const demandEndDate = project.demandEndDate
    ? new Date(project.demandEndDate).toLocaleDateString("en-US", {
        month: "2-digit",
        day: "2-digit",
        year: "numeric",
      })
    : null;

  return (
    <tr
      className="border-b border-border last:border-0 hover:bg-muted/30 transition-colors cursor-pointer"
      onClick={() =>
        navigate({ to: "/dashboard/projects/$projectId", params: { projectId: project.id } })
      }
    >
      <td className="px-4 py-3 text-muted-foreground" title={project.projectLead ?? undefined}>
        {project.projectLead ?? "—"}
      </td>
      <td className="px-4 py-3 text-muted-foreground" title={project.demand ?? undefined}>
        {project.demand ?? "—"}
      </td>
      <td className="px-4 py-3 text-muted-foreground">{demandEndDate ?? "—"}</td>
      <td className="px-4 py-3 text-muted-foreground" title={project.product ?? undefined}>
        {project.product ?? "—"}
      </td>
      <td className="px-4 py-3 text-muted-foreground">{project.countTotal}</td>
      <td className="px-4 py-3 text-muted-foreground">{project.totalComplete}</td>
      <td className="px-4 py-3 text-muted-foreground">
        {project.complianceRate === null ? "—" : `${Math.round(project.complianceRate * 100)}%`}
      </td>
      <td className="px-4 py-3 text-center" onClick={(e) => e.stopPropagation()}>
        <Button variant="ghost" size="icon" onClick={() => onShowTeam(project)}>
          <Users className="h-4 w-4" />
          <span className="sr-only">View team</span>
        </Button>
      </td>
      {canCreateAudit && (
        <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
          <div className="flex items-center justify-end gap-2">
            <Button size="sm" variant="outline" onClick={() => onRequestGuidance?.(project.id)}>
              <MessageCircleQuestion className="h-4 w-4 mr-1" />
              Request Guidance
            </Button>
            <Button
              size="sm"
              disabled={isLoading}
              onClick={() => {
                setIsLoading(true);
                setTimeout(() => onCreateAudit?.(project.id), 0);
              }}
            >
              {isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <Plus className="h-4 w-4 mr-1" />
                  New Audit
                </>
              )}
            </Button>
          </div>
        </td>
      )}
    </tr>
  );
}

export const ProjectsList = memo(function ProjectsList({
  canCreateAudit,
  onCreateAudit,
  onRequestGuidance,
}: ProjectsListProps) {
  const { data: session } = useSession();
  const { data: projects, isLoading, isError, refetch } = useAllProjects();
  const { data: audits } = useAudits();
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedQuery = useDebouncedValue(searchQuery, 200);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
  const [sortKey, setSortKey] = useState<SortKey>("projectLead");
  const [sortDir, setSortDir] = useState<SortDir>("asc");
  const [teamDialogProject, setTeamDialogProject] = useState<Project | null>(null);

  const handleSort = (col: SortKey) => {
    if (col === sortKey) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(col);
      setSortDir("asc");
    }
  };

  const columns: ResizableColumn[] = [
    { key: "projectLead", defaultWidth: 160 },
    { key: "demand", defaultWidth: 280 },
    { key: "demandEndDate", defaultWidth: 150 },
    { key: "product", defaultWidth: 130 },
    { key: "countTotal", defaultWidth: 190 },
    { key: "totalComplete", defaultWidth: 200 },
    { key: "complianceRate", defaultWidth: 170 },
    { key: "team", defaultWidth: 70, minWidth: 60 },
    ...(canCreateAudit ? [{ key: "actions", defaultWidth: 340, minWidth: 300 }] : []),
  ];
  const { widths, startResize } = useResizableColumns("projects-list", columns);

  const isAuditor = session?.user?.role === "compliance_auditor";
  const userId = session?.user?.id;

  const roleFilteredProjects = useMemo(() => {
    if (!isAuditor || !userId || !projects) return projects ?? [];
    const assignedProjectIds = new Set(
      (audits ?? [])
        .filter((a) => a.assignedToId === userId && a.projectId !== null)
        .map((a) => a.projectId as string),
    );
    return projects.filter((p) => assignedProjectIds.has(p.id));
  }, [isAuditor, userId, projects, audits]);

  const filterOptions = useMemo(
    () => ({
      projectLeads: uniqueSorted(roleFilteredProjects.map((p) => p.projectLead)),
      demands: uniqueSorted(roleFilteredProjects.map((p) => p.demand)),
      products: uniqueSorted(roleFilteredProjects.map((p) => p.product)),
    }),
    [roleFilteredProjects],
  );

  const setFilter = <K extends keyof Filters>(key: K, value: Filters[K]) =>
    setFilters((prev) => ({ ...prev, [key]: value }));

  const clearFilters = () => setFilters(EMPTY_FILTERS);

  const visibleProjects = useMemo(() => {
    const q = debouncedQuery.trim().toLowerCase();
    const filtered = roleFilteredProjects.filter((p) => {
      if (q && !p.name.toLowerCase().includes(q)) return false;
      if (filters.projectLead && p.projectLead !== filters.projectLead) return false;
      if (filters.demand && p.demand !== filters.demand) return false;
      if (filters.product && p.product !== filters.product) return false;
      if (filters.endDateMin || filters.endDateMax) {
        if (!p.demandEndDate) return false;
        const d = new Date(p.demandEndDate);
        if (filters.endDateMin && d < new Date(filters.endDateMin)) return false;
        if (filters.endDateMax && d > new Date(filters.endDateMax)) return false;
      }
      if (filters.scopeMin && p.countTotal < Number(filters.scopeMin)) return false;
      if (filters.scopeMax && p.countTotal > Number(filters.scopeMax)) return false;
      if (filters.completedMin && p.totalComplete < Number(filters.completedMin)) return false;
      if (filters.completedMax && p.totalComplete > Number(filters.completedMax)) return false;
      if (filters.rateMin || filters.rateMax) {
        if (p.complianceRate === null) return false;
        const ratePercent = p.complianceRate * 100;
        if (filters.rateMin && ratePercent < Number(filters.rateMin)) return false;
        if (filters.rateMax && ratePercent > Number(filters.rateMax)) return false;
      }
      return true;
    });

    return [...filtered].sort((a, b) => {
      if (sortKey === "demandEndDate") {
        const at = a.demandEndDate ? new Date(a.demandEndDate).getTime() : 0;
        const bt = b.demandEndDate ? new Date(b.demandEndDate).getTime() : 0;
        return sortDir === "asc" ? at - bt : bt - at;
      }
      if (sortKey === "countTotal" || sortKey === "totalComplete" || sortKey === "complianceRate") {
        const av = a[sortKey] ?? 0;
        const bv = b[sortKey] ?? 0;
        return sortDir === "asc" ? av - bv : bv - av;
      }
      const av = (a[sortKey] ?? "").toLowerCase();
      const bv = (b[sortKey] ?? "").toLowerCase();
      const cmp = av.localeCompare(bv);
      return sortDir === "asc" ? cmp : -cmp;
    });
  }, [roleFilteredProjects, debouncedQuery, filters, sortKey, sortDir]);

  const numActiveFilters = activeFilterCount(filters);
  const hasAnyFilter = numActiveFilters > 0 || debouncedQuery.trim() !== "";

  if (isError) {
    return <ErrorState message="Failed to load projects" onRetry={() => refetch()} />;
  }

  if (isLoading) {
    return (
      <div className="rounded-md border border-border overflow-x-auto contain-layout">
        <table className="w-full min-w-[900px] text-sm">
          <thead className="bg-muted/50 border-b border-border">
            <tr>
              {[
                "Project Lead",
                "Demand",
                "Demand End Date",
                "Product",
                "Projects in Scope",
                "Projects Completed",
                "Compliance Rate",
                "Team",
              ].map((h) => (
                <th key={h} className="px-4 py-3 text-left font-medium text-muted-foreground">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {["sk-1", "sk-2", "sk-3"].map((rk) => (
              <tr key={rk} className="border-b border-border last:border-0">
                {["c1", "c2", "c3", "c4", "c5", "c6", "c7", "c8"].map((ck) => (
                  <td key={ck} className="px-4 py-3">
                    <div className="h-4 bg-muted rounded animate-pulse" />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  if (roleFilteredProjects.length === 0 && !isLoading) {
    return (
      <EmptyState
        icon={FolderOpen}
        title={isAuditor ? "No projects assigned to you" : "No projects yet"}
        description={
          isAuditor
            ? "Projects with audits assigned to you will appear here."
            : "Projects will appear here once they have been synced from Delyver."
        }
      />
    );
  }

  return (
    <div className="space-y-4">
      {/* Search + filter toggle row */}
      <div className="flex items-center gap-3 flex-wrap">
        <SearchInput
          value={searchQuery}
          onValueChange={setSearchQuery}
          placeholder="Search by project…"
          className="max-w-sm"
        />
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowFilters((v) => !v)}
          className="gap-2"
        >
          <SlidersHorizontal className="h-4 w-4" />
          Filters
          {numActiveFilters > 0 && (
            <span className="ml-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-[10px] font-medium text-primary-foreground">
              {numActiveFilters}
            </span>
          )}
        </Button>
        {hasAnyFilter && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              clearFilters();
              setSearchQuery("");
            }}
            className="gap-1 text-muted-foreground"
          >
            <X className="h-3.5 w-3.5" />
            Clear all
          </Button>
        )}
        <span className="text-sm text-muted-foreground whitespace-nowrap ml-auto">
          {visibleProjects.length}
          {hasAnyFilter ? ` of ${roleFilteredProjects.length}` : ""}{" "}
          {visibleProjects.length === 1 ? "project" : "projects"}
        </span>
      </div>

      {/* Expanded filter panel */}
      {showFilters && (
        <div className="flex flex-wrap gap-4 rounded-md border border-border bg-muted/20 px-4 py-3">
          <FilterSelect
            label="Project Lead"
            value={filters.projectLead || "_all"}
            onValueChange={(v) => setFilter("projectLead", v === "_all" ? "" : v)}
            options={filterOptions.projectLeads}
          />
          <FilterSelect
            label="Demand"
            value={filters.demand || "_all"}
            onValueChange={(v) => setFilter("demand", v === "_all" ? "" : v)}
            options={filterOptions.demands}
          />
          <FilterSelect
            label="Product"
            value={filters.product || "_all"}
            onValueChange={(v) => setFilter("product", v === "_all" ? "" : v)}
            options={filterOptions.products}
          />

          {/* Date range */}
          <div className="flex flex-col gap-1">
            <Label className="text-xs text-muted-foreground">End Date From</Label>
            <Input
              type="date"
              value={filters.endDateMin}
              onChange={(e) => setFilter("endDateMin", e.target.value)}
              className="h-8 w-[150px] text-sm"
              max={filters.endDateMax || undefined}
            />
          </div>
          <div className="flex flex-col gap-1">
            <Label className="text-xs text-muted-foreground">End Date To</Label>
            <Input
              type="date"
              value={filters.endDateMax}
              onChange={(e) => setFilter("endDateMax", e.target.value)}
              className="h-8 w-[150px] text-sm"
              min={filters.endDateMin || undefined}
            />
          </div>

          {/* Projects in Scope range */}
          <div className="flex flex-col gap-1">
            <Label className="text-xs text-muted-foreground">Scope Min</Label>
            <Input
              type="number"
              min={0}
              value={filters.scopeMin}
              onChange={(e) => setFilter("scopeMin", e.target.value)}
              className="h-8 w-[100px] text-sm"
            />
          </div>
          <div className="flex flex-col gap-1">
            <Label className="text-xs text-muted-foreground">Scope Max</Label>
            <Input
              type="number"
              min={0}
              value={filters.scopeMax}
              onChange={(e) => setFilter("scopeMax", e.target.value)}
              className="h-8 w-[100px] text-sm"
            />
          </div>

          {/* Projects Completed range */}
          <div className="flex flex-col gap-1">
            <Label className="text-xs text-muted-foreground">Completed Min</Label>
            <Input
              type="number"
              min={0}
              value={filters.completedMin}
              onChange={(e) => setFilter("completedMin", e.target.value)}
              className="h-8 w-[100px] text-sm"
            />
          </div>
          <div className="flex flex-col gap-1">
            <Label className="text-xs text-muted-foreground">Completed Max</Label>
            <Input
              type="number"
              min={0}
              value={filters.completedMax}
              onChange={(e) => setFilter("completedMax", e.target.value)}
              className="h-8 w-[100px] text-sm"
            />
          </div>

          {/* Compliance Rate range (%) */}
          <div className="flex flex-col gap-1">
            <Label className="text-xs text-muted-foreground">Compliance % Min</Label>
            <Input
              type="number"
              min={0}
              max={100}
              value={filters.rateMin}
              onChange={(e) => setFilter("rateMin", e.target.value)}
              className="h-8 w-[100px] text-sm"
            />
          </div>
          <div className="flex flex-col gap-1">
            <Label className="text-xs text-muted-foreground">Compliance % Max</Label>
            <Input
              type="number"
              min={0}
              max={100}
              value={filters.rateMax}
              onChange={(e) => setFilter("rateMax", e.target.value)}
              className="h-8 w-[100px] text-sm"
            />
          </div>

          {numActiveFilters > 0 && (
            <div className="flex items-end">
              <Button
                variant="ghost"
                size="sm"
                onClick={clearFilters}
                className="gap-1 text-muted-foreground"
              >
                <X className="h-3.5 w-3.5" />
                Reset filters
              </Button>
            </div>
          )}
        </div>
      )}

      <div className="rounded-md border border-border overflow-x-auto contain-layout">
        {visibleProjects.length === 0 ? (
          <div className="py-12 text-center text-sm text-muted-foreground">
            No projects match the current filters
          </div>
        ) : (
          <table className="w-full table-fixed text-sm">
            <thead className="bg-muted/50 border-b border-border">
              <tr>
                <ResizableTh width={widths.projectLead} onResizeStart={startResize("projectLead")}>
                  <SortButton
                    label="Project Lead"
                    col="projectLead"
                    currentKey={sortKey}
                    currentDir={sortDir}
                    onSort={handleSort}
                  />
                </ResizableTh>
                <ResizableTh width={widths.demand} onResizeStart={startResize("demand")}>
                  <SortButton
                    label="Demand"
                    col="demand"
                    currentKey={sortKey}
                    currentDir={sortDir}
                    onSort={handleSort}
                  />
                </ResizableTh>
                <ResizableTh
                  width={widths.demandEndDate}
                  onResizeStart={startResize("demandEndDate")}
                >
                  <SortButton
                    label="Demand End Date"
                    col="demandEndDate"
                    currentKey={sortKey}
                    currentDir={sortDir}
                    onSort={handleSort}
                  />
                </ResizableTh>
                <ResizableTh width={widths.product} onResizeStart={startResize("product")}>
                  <SortButton
                    label="Product"
                    col="product"
                    currentKey={sortKey}
                    currentDir={sortDir}
                    onSort={handleSort}
                  />
                </ResizableTh>
                <ResizableTh width={widths.countTotal} onResizeStart={startResize("countTotal")}>
                  <SortButton
                    label="Projects in Scope"
                    col="countTotal"
                    currentKey={sortKey}
                    currentDir={sortDir}
                    onSort={handleSort}
                  />
                </ResizableTh>
                <ResizableTh
                  width={widths.totalComplete}
                  onResizeStart={startResize("totalComplete")}
                >
                  <SortButton
                    label="Projects Completed"
                    col="totalComplete"
                    currentKey={sortKey}
                    currentDir={sortDir}
                    onSort={handleSort}
                  />
                </ResizableTh>
                <ResizableTh
                  width={widths.complianceRate}
                  onResizeStart={startResize("complianceRate")}
                >
                  <SortButton
                    label="Compliance Rate"
                    col="complianceRate"
                    currentKey={sortKey}
                    currentDir={sortDir}
                    onSort={handleSort}
                  />
                </ResizableTh>
                <ResizableTh
                  width={widths.team}
                  onResizeStart={startResize("team")}
                  className="text-center"
                >
                  Team
                </ResizableTh>
                {canCreateAudit && (
                  <ResizableTh width={widths.actions} onResizeStart={startResize("actions")} />
                )}
              </tr>
            </thead>
            <tbody>
              {visibleProjects.map((project) => (
                <ProjectRow
                  key={project.id}
                  project={project}
                  canCreateAudit={canCreateAudit}
                  onCreateAudit={onCreateAudit}
                  onRequestGuidance={onRequestGuidance}
                  onShowTeam={setTeamDialogProject}
                />
              ))}
            </tbody>
          </table>
        )}
      </div>

      {teamDialogProject && (
        <ProjectTeamDialog
          open
          onOpenChange={(open) => !open && setTeamDialogProject(null)}
          project={teamDialogProject}
        />
      )}
    </div>
  );
});
